import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import { FiX, FiArrowRight, FiArrowLeft } from 'react-icons/fi';
import { tutorialAPI } from '../services/api';
import { useTutorial } from '../contexts/TutorialContext';
import './Tutorial.css';

/** Viewport at or below this width uses the dedicated mobile tour (desktop UI unchanged). */
const MOBILE_TUTORIAL_MAX_WIDTH_PX = 768;

interface TutorialStep {
  id: string;
  title: string;
  description: string;
  targetSelector: string;
  position: 'top' | 'bottom' | 'left' | 'right' | 'center';
  navigateTo?: string;
  requiresNavigation?: boolean;
  requiresClick?: boolean;
}

const TUTORIAL_STEPS: TutorialStep[] = [
  {
    id: 'profile',
    title: 'Profile & Subscription Status',
    description:
      'Here you can view your profile information and check your subscription status. Your subscription status is displayed next to your profile in the header.',
    targetSelector: '.dashboard-header-right',
    position: 'bottom',
  },
  {
    id: 'dashboard',
    title: 'Dashboard',
    description:
      'This is your main dashboard where you can see your test statistics, quick actions, and access all features of the platform.',
    targetSelector: '.dashboard-content',
    position: 'center',
    navigateTo: '/test-rules',
    requiresNavigation: true,
  },
  {
    id: 'test-rules',
    title: 'Test Rules',
    description:
      'This page contains all the testing guidelines and requirements. Please read through the rules carefully before starting your tests. You can download the rules document if needed.',
    targetSelector: '.dashboard-content',
    position: 'center',
    navigateTo: '/material/structured-segments',
    requiresNavigation: true,
  },
  {
    id: 'material',
    title: 'Material',
    description:
      'This section contains all your learning resources, including dialogue categories, structural segments, and podcasts. Explore these materials to help you prepare for your tests.',
    targetSelector: '.dashboard-content',
    position: 'center',
    navigateTo: '/booking',
    requiresNavigation: true,
  },
  {
    id: 'booking',
    title: 'Booking',
    description:
      'Here you can book sessions and services. Browse available services and request a booking for your preferred date and time. Your booking requests will be reviewed and confirmed via email.',
    targetSelector: '.dashboard-content',
    position: 'center',
  },
];

interface TutorialProps {
  onComplete: () => void;
  onSkip: () => void;
  initialStep?: number;
}

function useMobileTutorialViewport(): boolean {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth <= MOBILE_TUTORIAL_MAX_WIDTH_PX : false
  );

  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${MOBILE_TUTORIAL_MAX_WIDTH_PX}px)`);
    const onChange = () => setIsMobile(mq.matches);
    onChange();
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  return isMobile;
}

const Tutorial: React.FC<TutorialProps> = ({ onComplete, onSkip, initialStep = 0 }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isMobileTour = useMobileTutorialViewport();
  const { currentTutorialStep, nextTutorialStep, previousTutorialStep, setWaitingForClick } = useTutorial();
  const [currentStep, setCurrentStep] = useState(initialStep || currentTutorialStep);
  const [targetElement, setTargetElement] = useState<HTMLElement | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const overlayRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setCurrentStep(currentTutorialStep);
  }, [currentTutorialStep]);

  useEffect(() => {
    if (isMobileTour) {
      setTargetElement(null);
      return;
    }
    const timer = setTimeout(() => {
      updateTargetElement();
    }, 100);

    window.addEventListener('resize', updateTargetElement);
    window.addEventListener('scroll', updateTargetElement, true);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', updateTargetElement);
      window.removeEventListener('scroll', updateTargetElement, true);
    };
  }, [currentStep, isMobileTour]);

  useEffect(() => {
    setWaitingForClick(null);
  }, [currentStep, setWaitingForClick]);

  useEffect(() => {
    if (!isMobileTour) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isMobileTour]);

  const updateTargetElement = () => {
    if (isMobileTour) return;
    const step = TUTORIAL_STEPS[currentStep];
    if (!step) return;

    let element: HTMLElement | null = null;

    if (step.targetSelector.startsWith('.')) {
      element = document.querySelector(step.targetSelector) as HTMLElement;
    } else if (step.targetSelector.startsWith('[')) {
      element = document.querySelector(step.targetSelector) as HTMLElement;
    }

    if (!element && currentStep < TUTORIAL_STEPS.length) {
      setTimeout(() => {
        let el: HTMLElement | null = null;
        if (step.targetSelector.startsWith('.')) {
          el = document.querySelector(step.targetSelector) as HTMLElement;
        } else if (step.targetSelector.startsWith('[')) {
          el = document.querySelector(step.targetSelector) as HTMLElement;
        }
        setTargetElement(el);
        if (el && tooltipRef.current) {
          updateTooltipPosition(el, step);
        }
      }, 200);
      return;
    }

    setTargetElement(element);

    if (element && tooltipRef.current) {
      updateTooltipPosition(element, step);
    }
  };

  const updateTooltipPosition = (element: HTMLElement, step: TutorialStep) => {
    if (!tooltipRef.current) return;

    const rect = element.getBoundingClientRect();
    const tooltipRect = tooltipRef.current.getBoundingClientRect();

    let top = 0;
    let left = 0;

    switch (step.position) {
      case 'top':
        top = rect.top - tooltipRect.height - 20;
        left = rect.left + rect.width / 2 - tooltipRect.width / 2;
        break;
      case 'bottom':
        top = rect.bottom + 20;
        left = rect.left + rect.width / 2 - tooltipRect.width / 2;
        break;
      case 'left':
        top = rect.top + rect.height / 2 - tooltipRect.height / 2;
        left = rect.left - tooltipRect.width - 20;
        break;
      case 'right':
        top = rect.top + rect.height / 2 - tooltipRect.height / 2;
        left = rect.right + 20;
        break;
      case 'center':
        top = window.innerHeight / 2 - tooltipRect.height / 2;
        left = window.innerWidth / 2 - tooltipRect.width / 2;
        break;
    }

    top = Math.max(20, Math.min(top, window.innerHeight - tooltipRect.height - 20));
    left = Math.max(20, Math.min(left, window.innerWidth - tooltipRect.width - 20));

    setTooltipPosition({ top, left });
  };

  useLayoutEffect(() => {
    if (isMobileTour || !targetElement || !tooltipRef.current) return;
    const step = TUTORIAL_STEPS[currentStep];
    if (step) updateTooltipPosition(targetElement, step);
  }, [currentStep, targetElement, isMobileTour]);

  const handleNext = async () => {
    const step = TUTORIAL_STEPS[currentStep];

    if (step.requiresNavigation && step.navigateTo) {
      if (location.pathname !== step.navigateTo) {
        navigate(step.navigateTo);
        setTimeout(() => {
          if (currentStep < TUTORIAL_STEPS.length - 1) {
            setCurrentStep(currentStep + 1);
            nextTutorialStep();
          }
        }, 500);
        return;
      }
    }

    if (currentStep < TUTORIAL_STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
      nextTutorialStep();
    } else {
      await handleComplete();
    }
  };

  const handlePrevious = () => {
    const step = TUTORIAL_STEPS[currentStep];

    if (step.requiresNavigation && location.pathname !== '/dashboard') {
      navigate('/dashboard');
      setTimeout(() => {
        if (currentStep > 0) {
          setCurrentStep(currentStep - 1);
          previousTutorialStep();
        }
      }, 300);
      return;
    }

    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
      previousTutorialStep();
    }
  };

  const handleComplete = async () => {
    try {
      await tutorialAPI.completeTutorial();
      onComplete();
    } catch (error) {
      console.error('Error completing tutorial:', error);
      onComplete();
    }
  };

  const handleSkip = async () => {
    try {
      await tutorialAPI.completeTutorial();
      onSkip();
    } catch (error) {
      console.error('Error skipping tutorial:', error);
      onSkip();
    }
  };

  const currentStepData = TUTORIAL_STEPS[currentStep];
  const progressRatio = (currentStep + 1) / TUTORIAL_STEPS.length;

  if (isMobileTour) {
    return (
      <AnimatePresence>
        <motion.div
          ref={overlayRef}
          className="tutorial-mobile-root"
          role="dialog"
          aria-modal="true"
          aria-labelledby="tutorial-mobile-title"
          aria-describedby="tutorial-mobile-desc"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
        >
          <motion.div
            className="tutorial-mobile-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            aria-hidden="true"
          />
          <motion.div
            className="tutorial-mobile-sheet"
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 28, stiffness: 320 }}
          >
            <div className="tutorial-mobile-sheet-handle" aria-hidden="true" />
            <div className="tutorial-mobile-top">
              <span className="tutorial-mobile-eyebrow">Quick tour</span>
              <button type="button" className="tutorial-mobile-icon-close" onClick={handleSkip} aria-label="Skip tutorial">
                {React.createElement(FiX as React.ComponentType<{ size?: number }>, { size: 22 })}
              </button>
            </div>
            <div className="tutorial-mobile-progress-track" aria-hidden="true">
              <div className="tutorial-mobile-progress-fill" style={{ width: `${progressRatio * 100}%` }} />
            </div>
            <div className="tutorial-mobile-meta">
              Step <strong>{currentStep + 1}</strong> of {TUTORIAL_STEPS.length}
            </div>
            <div className="tutorial-mobile-body">
              <h3 id="tutorial-mobile-title" className="tutorial-mobile-title">
                {currentStepData.title}
              </h3>
              <p id="tutorial-mobile-desc" className="tutorial-mobile-description">
                {currentStepData.description}
              </p>
            </div>
            <div className="tutorial-mobile-actions">
              <button type="button" className="tutorial-mobile-btn-skip" onClick={handleSkip}>
                Skip tour
              </button>
              <div className="tutorial-mobile-nav-row">
                {currentStep > 0 ? (
                  <button type="button" className="tutorial-mobile-btn secondary" onClick={handlePrevious}>
                    {React.createElement(FiArrowLeft as React.ComponentType<{ size?: number }>, { size: 20 })}
                    Back
                  </button>
                ) : (
                  <span className="tutorial-mobile-nav-spacer" />
                )}
                <button type="button" className="tutorial-mobile-btn primary" onClick={handleNext}>
                  {currentStep < TUTORIAL_STEPS.length - 1 ? (
                    <>
                      Next
                      {React.createElement(FiArrowRight as React.ComponentType<{ size?: number }>, { size: 20 })}
                    </>
                  ) : (
                    'Get started'
                  )}
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </AnimatePresence>
    );
  }

  return (
    <AnimatePresence>
      <motion.div
        ref={overlayRef}
        className="tutorial-overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.3 }}
      >
        <svg
          className="tutorial-blur-svg"
          style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none' }}
        >
          <defs>
            <filter id="blur">
              <feGaussianBlur in="SourceGraphic" stdDeviation="5" />
            </filter>
            <mask id="cutout">
              <rect width="100%" height="100%" fill="white" />
              {targetElement &&
                (() => {
                  const rect = targetElement.getBoundingClientRect();
                  return (
                    <rect
                      x={rect.left}
                      y={rect.top}
                      width={rect.width}
                      height={rect.height}
                      fill="black"
                      rx="8"
                    />
                  );
                })()}
            </mask>
          </defs>
          <rect width="100%" height="100%" fill="rgba(0, 0, 0, 0.7)" filter="url(#blur)" mask="url(#cutout)" />
        </svg>

        {targetElement && (
          <motion.div
            className="tutorial-highlight"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.3 }}
            style={{
              position: 'absolute',
              top: targetElement.getBoundingClientRect().top - 4,
              left: targetElement.getBoundingClientRect().left - 4,
              width: targetElement.getBoundingClientRect().width + 8,
              height: targetElement.getBoundingClientRect().height + 8,
              border: '3px solid #E63946',
              borderRadius: '8px',
              pointerEvents: 'none',
              boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.5)',
            }}
          />
        )}

        <motion.div
          ref={tooltipRef}
          className="tutorial-tooltip"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 20 }}
          transition={{ duration: 0.3 }}
          style={{
            top: currentStepData.position === 'center' ? '50%' : `${tooltipPosition.top}px`,
            left: currentStepData.position === 'center' ? '50%' : `${tooltipPosition.left}px`,
            transform: currentStepData.position === 'center' ? 'translate(-50%, -50%)' : 'none',
          }}
        >
          <div className="tutorial-tooltip-header">
            <div className="tutorial-progress">
              <span className="tutorial-step-number">{currentStep + 1}</span>
              <span className="tutorial-step-total">/ {TUTORIAL_STEPS.length}</span>
            </div>
            <button className="tutorial-close-btn" onClick={handleSkip} aria-label="Skip tutorial">
              {React.createElement(FiX as React.ComponentType<{ size?: number }>, { size: 20 })}
            </button>
          </div>

          <div className="tutorial-tooltip-content">
            <h3 className="tutorial-title">{currentStepData.title}</h3>
            <p className="tutorial-description">{currentStepData.description}</p>
          </div>

          <div className="tutorial-tooltip-footer">
            <button className="tutorial-btn tutorial-btn-skip" onClick={handleSkip}>
              Skip Tutorial
            </button>
            <div className="tutorial-nav-buttons">
              {currentStep > 0 && (
                <button className="tutorial-btn tutorial-btn-secondary" onClick={handlePrevious}>
                  {React.createElement(FiArrowLeft as React.ComponentType<{ size?: number }>, { size: 18 })}
                  Previous
                </button>
              )}
              <button className="tutorial-btn tutorial-btn-primary" onClick={handleNext}>
                {currentStep < TUTORIAL_STEPS.length - 1 ? (
                  <>
                    Next
                    {React.createElement(FiArrowRight as React.ComponentType<{ size?: number }>, { size: 18 })}
                  </>
                ) : (
                  'Finish'
                )}
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default Tutorial;
